import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { scrollableTableWrapStyles, scrollableTableSizing } from '../styles/scrollableTable';
import { FiCheck, FiX, FiEye, FiFileText, FiUpload, FiSearch } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';
import { format } from 'date-fns';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 24px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const TableWrapper = styled.div`
  ${scrollableTableWrapStyles}
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f9fafb;
`;

const TableHeaderRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #1f2937;
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'paid': return '#dcfce7';
      case 'approved': return '#dbeafe';
      case 'pending': return '#fef3c7';
      case 'rejected': return '#fee2e2';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'paid': return '#166534';
      case 'approved': return '#1e40af';
      case 'pending': return '#92400e';
      case 'rejected': return '#dc2626';
      default: return '#6b7280';
    }
  }};
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 8px;

  ${props => {
    if (props.variant === 'success') {
      return `
        background: #10b981;
        color: white;
        &:hover:not(:disabled) {
          background: #059669;
        }
      `;
    }
    if (props.variant === 'danger') {
      return `
        background: #ef4444;
        color: white;
        &:hover:not(:disabled) {
          background: #dc2626;
        }
      `;
    }
    if (props.variant === 'primary') {
      return `
        background: #5efba9;
        color: #1f2937;
        &:hover:not(:disabled) {
          background: #4de89e;
        }
      `;
    }
    return `
      background: white;
      color: #6b7280;
      border: 1px solid #e5e7eb;
      &:hover:not(:disabled) {
        background: #f9fafb;
      }
    `;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: #9ca3af;
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 18px;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s;

  &:hover {
    color: #6b7280;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  border-radius: 6px;

  &:hover {
    background: #f3f4f6;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const TextArea = styled.textarea`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;

  &:hover {
    background: #f9fafb;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #6b7280;
`;

const InfoValue = styled.span`
  color: #1f2937;
`;

const UploadArea = styled.div`
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f9fafb;
  
  &:hover {
    border-color: #5efba9;
    background: #f0fdf4;
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  max-height: 200px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-top: 8px;
`;

function SuperAdminPayments() {
  const { session } = useAuthContext();
  const [brandsPayable, setBrandsPayable] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'approve', 'reject', 'record_payment'
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    payment_method: '',
    receipt_url: '',
    img: '',
    notes: '',
    paid_amount: ''
  });
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const receiptInputRef = React.createRef();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermRequests, setSearchTermRequests] = useState('');

  useEffect(() => {
    if (session) {
      fetchBrandsPayable();
      fetchPaymentRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, session]);

  const fetchBrandsPayable = async () => {
    try {
      console.log('📊 Fetching brands payable data...');
      
      // Fetch all active brands first
      const { data: allBrands, error: brandsError } = await supabase
        .from('brands')
        .select('id, name')
        .eq('active', true)
        .order('name', { ascending: true });

      if (brandsError) {
        console.error('Error fetching brands:', brandsError);
        throw brandsError;
      }

      console.log('All active brands:', allBrands?.length || 0);

      // For each brand, calculate their totals
      const brandsWithPayable = await Promise.all((allBrands || []).map(async (brand) => {
        console.log(`💰 Calculating for brand: ${brand.name} (${brand.id})`);
        
        // Get order items with order data - filter client side for reliability
        const { data: orderItems, error: orderItemsError } = await supabase
          .from('order_items')
          .select('line_total, order_id, shipment_status_name, orders(id, status)')
          .eq('brand_id', brand.id);

        if (orderItemsError) {
          console.error(`  - Error fetching order items:`, orderItemsError);
        }

        // Filter to include items from:
        // 1. Orders with status: completed, confirmed, or processing
        // 2. OR items with delivered shipment status
        const validStatuses = ['completed', 'confirmed', 'processing'];
        const validOrderItems = (orderItems || []).filter(item => {
          const orderStatus = item.orders?.status;
          const shipmentStatus = (item.shipment_status_name || '').toLowerCase();
          const isDelivered = shipmentStatus.includes('delivered') || shipmentStatus.includes('تم التسليم');
          
          return isDelivered || (orderStatus && validStatuses.includes(orderStatus));
        });

        console.log(`  - Total order_items for ${brand.name}:`, orderItems?.length || 0);
        console.log(`  - Valid order_items (completed/confirmed/processing/delivered):`, validOrderItems.length);

        const totalSold = validOrderItems.reduce((sum, item) => sum + parseFloat(item.line_total || 0), 0);

        console.log(`  - Total sold: OMR ${totalSold.toFixed(2)}`);

        // Get payment requests by status for this brand
        const { data: requests } = await supabase
          .from('brand_payment_requests')
          .select('amount, status')
          .eq('brand_id', brand.id);

        console.log(`  - Payment requests:`, requests?.length || 0);

        // Calculate by status (matching AdminPayments logic)
        let totalPaid = 0;
        let totalPending = 0;
        let totalRejected = 0;
        
        if (requests) {
          totalPaid = requests
            .filter(req => req.status === 'paid')
            .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0);
          
          totalPending = requests
            .filter(req => req.status === 'pending' || req.status === 'approved')
            .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0);
            
          totalRejected = requests
            .filter(req => req.status === 'rejected')
            .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0);
        }

        // Total requested excludes rejected (only paid + pending/approved)
        const totalRequested = totalPaid + totalPending;
        console.log(`  - Total paid: OMR ${totalPaid.toFixed(2)}`);
        console.log(`  - Total pending/approved: OMR ${totalPending.toFixed(2)}`);
        console.log(`  - Total rejected (excluded): OMR ${totalRejected.toFixed(2)}`);
        console.log(`  - Total requested (paid+pending): OMR ${totalRequested.toFixed(2)}`);

        // Remaining = Total Sold - (Paid + Pending/Approved)
        const remainingPayable = totalSold - totalRequested;
        console.log(`  - Remaining payable: OMR ${remainingPayable.toFixed(2)}`);

        return {
          brand_id: brand.id,
          brand: brand,
          total_sold: totalSold,
          total_paid: totalPaid,
          total_pending: totalPending,
          total_rejected: totalRejected,
          total_requested: totalRequested,
          remaining_payable: Math.max(0, remainingPayable) // Ensure not negative
        };
      }));

      console.log('Brands with payable data:', brandsWithPayable);
      setBrandsPayable(brandsWithPayable || []);
    } catch (error) {
      console.error('Error fetching brands payable:', error);
      setBrandsPayable([]);
    }
  };

  const fetchPaymentRequests = async () => {
    try {
      setLoading(true);
      console.log('📋 SuperAdmin: Fetching payment requests...');
      console.log('Filter status:', filterStatus);
      console.log('Current user:', session?.user?.id);
      
      // Check if user is superadmin
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_superadmin')
        .eq('id', session?.user?.id)
        .single();

      console.log('User profile:', profile);
      console.log('Is superadmin:', profile?.is_superadmin);
      
      // Simplified query - just get requests with brand info
      let query = supabase
        .from('brand_payment_requests')
        .select(`
          *,
          brands(id, name)
        `)
        .order('created_at', { ascending: false });

      // Apply filter if not 'all'
      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;

      console.log('Payment requests query result:', {
        data: data,
        count: data?.length || 0,
        error: error
      });

      if (error) {
        console.error('❌ Error fetching payment requests:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error hint:', error.hint);
        console.error('Error details:', error.details);
        
        // Check if it's an RLS error
        if (error.code === 'PGRST116' || error.message?.includes('policy')) {
          console.error('🚫 RLS POLICY IS BLOCKING ACCESS!');
          console.error('The app_role() function might not exist or is not returning "superadmin"');
          alert('RLS Policy Error: Unable to fetch payment requests. The database RLS policy may need to be updated to allow superadmin access based on is_superadmin column instead of app_role() function.');
        }
        
        setPaymentRequests([]);
      } else {
        console.log('✅ Payment requests fetched:', data?.length || 0);
        if (data && data.length > 0) {
          console.log('Sample request:', data[0]);
        }
        setPaymentRequests(data || []);
      }
    } catch (error) {
      console.error('Error fetching payment requests:', error);
      setPaymentRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setModalMode('view');
    setIsModalOpen(true);
  };

  const handleApproveRequest = (request) => {
    setSelectedRequest(request);
    setModalMode('approve');
    setIsModalOpen(true);
  };

  const handleRejectRequest = (request) => {
    setSelectedRequest(request);
    setModalMode('reject');
    setIsModalOpen(true);
  };

  const handleRecordPayment = (request) => {
    setSelectedRequest(request);
    setFormData({
      payment_method: '',
      receipt_url: '',
      img: '',
      notes: '',
      paid_amount: request.amount.toString()
    });
    setModalMode('record_payment');
    setIsModalOpen(true);
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingReceipt(true);
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `receipt_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

      console.log('Uploading receipt to receipt bucket:', fileName);

      // Upload to Supabase Storage (private bucket)
      const { data, error: uploadError } = await supabase.storage
        .from('receipt')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        alert('Failed to upload receipt: ' + uploadError.message);
        return;
      }

      console.log('Upload successful:', data);

      // Get signed URL (for private bucket) with 1 year expiration
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('receipt')
        .createSignedUrl(fileName, 31536000); // 1 year = 365 days * 24 hours * 3600 seconds

      if (signedUrlError) {
        console.error('Error creating signed URL:', signedUrlError);
        alert('Failed to generate receipt URL: ' + signedUrlError.message);
        return;
      }

      const signedUrl = signedUrlData?.signedUrl;
      console.log('Receipt signed URL:', signedUrl);

      // Set the img field with the signed URL
      setFormData(prev => ({
        ...prev,
        img: signedUrl
      }));

      // Reset file input
      if (receiptInputRef.current) {
        receiptInputRef.current.value = '';
      }

      alert('Receipt uploaded successfully!');
    } catch (err) {
      console.error('Error uploading receipt:', err);
      alert('Failed to upload receipt: ' + err.message);
    } finally {
      setUploadingReceipt(false);
    }
  };

  // Filter brands payable by search term
  const filteredBrandsPayable = useMemo(() => {
    if (!searchTerm.trim()) {
      return brandsPayable;
    }
    const term = searchTerm.toLowerCase();
    return brandsPayable.filter(item => {
      const brandName = (item.brand?.name || '').toLowerCase();
      return brandName.includes(term);
    });
  }, [brandsPayable, searchTerm]);

  // Filter payment requests by search term
  const filteredPaymentRequests = useMemo(() => {
    if (!searchTermRequests.trim()) {
      return paymentRequests;
    }
    const term = searchTermRequests.toLowerCase();
    return paymentRequests.filter(request => {
      const brandName = (request.brands?.name || '').toLowerCase();
      const amount = request.amount?.toString() || '';
      const status = (request.status || '').toLowerCase();
      return brandName.includes(term) || amount.includes(term) || status.includes(term);
    });
  }, [paymentRequests, searchTermRequests]);

  const handleUpdateStatus = async (status, notes = null) => {
    if (!session || !selectedRequest) return;

    setSubmitting(true);
    try {
      const updateData = {
        status,
        updated_at: new Date().toISOString()
      };

      if (notes) {
        updateData.notes = notes;
      }

      const { error } = await supabase
        .from('brand_payment_requests')
        .update(updateData)
        .eq('id', selectedRequest.id);

      if (error) throw error;

      alert(`Payment request ${status} successfully!`);
      setIsModalOpen(false);
      fetchPaymentRequests();
      fetchBrandsPayable();
    } catch (error) {
      console.error('Error updating request status:', error);
      alert(`Failed to ${status} request: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!session || !selectedRequest) return;

    setSubmitting(true);
    try {
      // First, update the payment request with the receipt image and status to 'paid'
      const updateData = {
        status: 'paid',
        updated_at: new Date().toISOString()
      };
      
      if (formData.img) {
        updateData.img = formData.img;
      }

      const { error: updateRequestError } = await supabase
        .from('brand_payment_requests')
        .update(updateData)
        .eq('id', selectedRequest.id);

      if (updateRequestError) {
        console.error('Error updating request status:', updateRequestError);
        throw updateRequestError;
      }

      console.log('✅ Payment request status updated to paid');

      // Insert payment record for audit trail
      const paymentData = {
        payment_request_id: selectedRequest.id,
        paid_by: session.user.id,
        paid_amount: parseFloat(formData.paid_amount),
        currency: selectedRequest.currency || 'OMR',
        payment_method: formData.payment_method || null,
        receipt_url: formData.receipt_url || null,
        notes: formData.notes || null
      };

      const { error } = await supabase
        .from('brand_payments')
        .insert([paymentData]);

      if (error) {
        console.error('Error inserting payment record:', error);
        // Don't throw - the status is already updated, payment record is for audit
      }

      alert('Payment recorded successfully!');
      setIsModalOpen(false);
      setFormData({
        payment_method: '',
        receipt_url: '',
        img: '',
        notes: '',
        paid_amount: ''
      });
      fetchPaymentRequests();
      fetchBrandsPayable();
    } catch (error) {
      console.error('Error recording payment:', error);
      alert(`Failed to record payment: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
    setModalMode('view');
    setFormData({
      payment_method: '',
      receipt_url: '',
      img: '',
      notes: '',
      paid_amount: ''
    });
  };

  return (
    <Container>
      <Section>
        <SectionTitle>Brands Payable Summary</SectionTitle>
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search brands..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <ClearButton onClick={() => setSearchTerm('')} title="Clear search">
              <FiX size={18} />
            </ClearButton>
          )}
        </SearchContainer>
        <TableWrapper>
          <Table>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>Brand</TableHeaderCell>
                <TableHeaderCell>Total Sold</TableHeaderCell>
                <TableHeaderCell>Paid</TableHeaderCell>
                <TableHeaderCell>Pending</TableHeaderCell>
                <TableHeaderCell>Rejected</TableHeaderCell>
                <TableHeaderCell>Remaining Payable</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {filteredBrandsPayable.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                    <EmptyText>{searchTerm ? 'No brands found matching your search' : 'No payable data available'}</EmptyText>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBrandsPayable.map((item) => (
                  <TableRow key={item.brand_id}>
                    <TableCell>
                      <strong>{item.brand?.name || 'Unknown Brand'}</strong>
                    </TableCell>
                    <TableCell>
                      OMR {parseFloat(item.total_sold || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <span style={{ color: '#10b981' }}>
                        OMR {parseFloat(item.total_paid || 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span style={{ color: '#f59e0b' }}>
                        OMR {parseFloat(item.total_pending || 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span style={{ color: '#ef4444' }}>
                        OMR {parseFloat(item.total_rejected || 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <strong style={{ color: '#5efba9' }}>
                        OMR {parseFloat(item.remaining_payable || 0).toFixed(2)}
                      </strong>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>Payment Requests</SectionTitle>
          <FilterContainer>
            <FilterSelect 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="paid">Paid</option>
            </FilterSelect>
          </FilterContainer>
        </SectionHeader>
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search payment requests by brand, amount, or status..."
            value={searchTermRequests}
            onChange={(e) => setSearchTermRequests(e.target.value)}
          />
          {searchTermRequests && (
            <ClearButton onClick={() => setSearchTermRequests('')} title="Clear search">
              <FiX size={18} />
            </ClearButton>
          )}
        </SearchContainer>

        <TableWrapper>
          <Table>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>Brand</TableHeaderCell>
                <TableHeaderCell>Amount</TableHeaderCell>
                <TableHeaderCell>Period</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Requested Date</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                    <EmptyText>Loading...</EmptyText>
                  </TableCell>
                </TableRow>
              ) : filteredPaymentRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="6" style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <EmptyText>{searchTermRequests ? 'No payment requests found matching your search' : 'No payment requests found'}</EmptyText>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPaymentRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <strong>{request.brands?.name || 'Unknown Brand'}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{request.currency} {parseFloat(request.amount).toFixed(2)}</strong>
                    </TableCell>
                    <TableCell>
                      {request.period_from && request.period_to ? (
                        <div style={{ fontSize: '12px' }}>
                          {format(new Date(request.period_from), 'MMM dd')} - {format(new Date(request.period_to), 'MMM dd, yyyy')}
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={request.status}>
                        {request.status}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      {request.created_at
                        ? format(new Date(request.created_at), 'MMM dd, yyyy')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <ActionButton
                          variant="default"
                          onClick={() => handleViewRequest(request)}
                          title="View Details"
                        >
                          <FiEye />
                        </ActionButton>
                        {request.status === 'pending' && (
                          <>
                            <ActionButton
                              variant="success"
                              onClick={() => handleApproveRequest(request)}
                              title="Approve"
                            >
                              <FiCheck />
                            </ActionButton>
                            <ActionButton
                              variant="danger"
                              onClick={() => handleRejectRequest(request)}
                              title="Reject"
                            >
                              <FiX />
                            </ActionButton>
                          </>
                        )}
                        {request.status === 'approved' && (
                          <ActionButton
                            variant="primary"
                            onClick={() => handleRecordPayment(request)}
                            title="Record Payment"
                          >
                            <FiFileText /> Pay
                          </ActionButton>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </Section>

      {/* Modal */}
      {isModalOpen && selectedRequest && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {modalMode === 'view' && 'Payment Request Details'}
                {modalMode === 'approve' && 'Approve Payment Request'}
                {modalMode === 'reject' && 'Reject Payment Request'}
                {modalMode === 'record_payment' && 'Record Payment'}
              </ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            {modalMode === 'view' && (
              <div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <InfoRow>
                    <InfoLabel>Brand:</InfoLabel>
                    <InfoValue>{selectedRequest.brands?.name || 'Unknown'}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Amount:</InfoLabel>
                    <InfoValue>
                      <strong>{selectedRequest.currency} {parseFloat(selectedRequest.amount).toFixed(2)}</strong>
                    </InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Period:</InfoLabel>
                    <InfoValue>
                      {selectedRequest.period_from && selectedRequest.period_to
                        ? `${format(new Date(selectedRequest.period_from), 'MMM dd, yyyy')} - ${format(new Date(selectedRequest.period_to), 'MMM dd, yyyy')}`
                        : 'N/A'}
                    </InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Status:</InfoLabel>
                    <InfoValue>
                      <StatusBadge status={selectedRequest.status}>
                        {selectedRequest.status}
                      </StatusBadge>
                    </InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Requested By:</InfoLabel>
                    <InfoValue>
                      <code style={{ fontSize: '11px' }}>{selectedRequest.requested_by || 'N/A'}</code>
                    </InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Requested Date:</InfoLabel>
                    <InfoValue>
                      {selectedRequest.created_at
                        ? format(new Date(selectedRequest.created_at), 'MMM dd, yyyy HH:mm')
                        : 'N/A'}
                    </InfoValue>
                  </InfoRow>
                  {selectedRequest.notes && (
                    <InfoRow>
                      <InfoLabel>Notes:</InfoLabel>
                      <InfoValue style={{ whiteSpace: 'pre-wrap' }}>{selectedRequest.notes}</InfoValue>
                    </InfoRow>
                  )}
                  {selectedRequest.img && (
                    <InfoRow style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                      <InfoLabel>Receipt Image:</InfoLabel>
                      <ImagePreview src={selectedRequest.img} alt="Payment receipt" />
                      <a 
                        href={selectedRequest.img} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ fontSize: '12px', color: '#5efba9', textDecoration: 'none' }}
                      >
                        Open in new tab →
                      </a>
                    </InfoRow>
                  )}
                </div>

                <ButtonGroup>
                  <CancelButton onClick={handleCloseModal}>Close</CancelButton>
                  {selectedRequest.status === 'pending' && (
                    <>
                      <ActionButton
                        variant="success"
                        onClick={() => handleApproveRequest(selectedRequest)}
                      >
                        <FiCheck /> Approve
                      </ActionButton>
                      <ActionButton
                        variant="danger"
                        onClick={() => handleRejectRequest(selectedRequest)}
                      >
                        <FiX /> Reject
                      </ActionButton>
                    </>
                  )}
                  {selectedRequest.status === 'approved' && (
                    <ActionButton
                      variant="primary"
                      onClick={() => handleRecordPayment(selectedRequest)}
                    >
                      <FiFileText /> Record Payment
                    </ActionButton>
                  )}
                </ButtonGroup>
              </div>
            )}

            {(modalMode === 'approve' || modalMode === 'reject') && (
              <Form onSubmit={(e) => {
                e.preventDefault();
                const notes = e.target.notes?.value || null;
                handleUpdateStatus(modalMode === 'approve' ? 'approved' : 'rejected', notes);
              }}>
                <FormGroup>
                  <Label>Notes (Optional)</Label>
                  <TextArea
                    name="notes"
                    placeholder="Add notes about this decision..."
                  />
                </FormGroup>

                <ButtonGroup>
                  <CancelButton type="button" onClick={handleCloseModal}>Cancel</CancelButton>
                  <ActionButton
                    type="submit"
                    variant={modalMode === 'approve' ? 'success' : 'danger'}
                    disabled={submitting}
                  >
                    {modalMode === 'approve' ? (
                      <>
                        <FiCheck /> {submitting ? 'Approving...' : 'Approve Request'}
                      </>
                    ) : (
                      <>
                        <FiX /> {submitting ? 'Rejecting...' : 'Reject Request'}
                      </>
                    )}
                  </ActionButton>
                </ButtonGroup>
              </Form>
            )}

            {modalMode === 'record_payment' && (
              <Form onSubmit={handleSubmitPayment}>
                <FormGroup>
                  <Label>Paid Amount *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.paid_amount}
                    onChange={(e) => setFormData({ ...formData, paid_amount: e.target.value })}
                    required
                  />
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    Requested: {selectedRequest.currency} {parseFloat(selectedRequest.amount).toFixed(2)}
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Payment Method</Label>
                  <Input
                    type="text"
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    placeholder="e.g., Bank Transfer, Cash, etc."
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Receipt Image (Upload to Bucket)</Label>
                  <input
                    ref={receiptInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleReceiptUpload}
                    disabled={uploadingReceipt}
                    style={{ display: 'none' }}
                  />
                  <UploadArea onClick={() => !uploadingReceipt && receiptInputRef.current?.click()}>
                    {uploadingReceipt ? (
                      <div>
                        <p style={{ color: '#5efba9', margin: 0 }}>Uploading...</p>
                      </div>
                    ) : (
                      <>
                        <FiUpload style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '12px' }} />
                        <p style={{ color: '#6b7280', margin: 0 }}>
                          Click to upload receipt image/PDF
                        </p>
                        <p style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0 0 0' }}>
                          Supports: JPG, PNG, PDF
                        </p>
                      </>
                    )}
                  </UploadArea>
                  {formData.img && (
                    <>
                      <ImagePreview src={formData.img} alt="Receipt preview" />
                      <Input
                        type="text"
                        value={formData.img}
                        readOnly
                        style={{ marginTop: '8px', fontSize: '11px', fontFamily: 'monospace', background: '#f9fafb' }}
                      />
                    </>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Receipt URL (Optional - if not uploading)</Label>
                  <Input
                    type="url"
                    value={formData.receipt_url}
                    onChange={(e) => setFormData({ ...formData, receipt_url: e.target.value })}
                    placeholder="https://..."
                  />
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                    Leave empty if using uploaded receipt above
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Notes</Label>
                  <TextArea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes about this payment..."
                  />
                </FormGroup>

                <ButtonGroup>
                  <CancelButton type="button" onClick={handleCloseModal}>Cancel</CancelButton>
                  <ActionButton
                    type="submit"
                    variant="primary"
                    disabled={submitting}
                  >
                    <FiFileText /> {submitting ? 'Recording...' : 'Record Payment'}
                  </ActionButton>
                </ButtonGroup>
              </Form>
            )}
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
  );
}

export default SuperAdminPayments;
